import React, { useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Box, Typography, Button, Card, CardActions, CardContent, Badge } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import NotificationsIcon from '@mui/icons-material/Notifications'
import SettingsIcon from '@mui/icons-material/Settings'
import CloseIcon from '@mui/icons-material/Close'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import EditIcon from '@mui/icons-material/Edit'
import TuneIcon from '@mui/icons-material/Tune'
import AlertConditionStatus from './AlertConditionStatus'
import { useAlertContext } from '../contexts/AlertContext'
import { Alert, useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { Tabs as AppTabs } from '../../../layout/MainLayout'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { AccountBalance } from '@mui/icons-material'
import { doc, updateDoc } from 'firebase/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import PatternVersionTitle from '../../../components/backtests/PatternVersionTitle'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import { useModalContext } from '../../../contexts/ModalProvider'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import { SettingsSwitchRow } from './tabs/SettingsTab'
import LockIcon from '@mui/icons-material/Lock'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AutotraderUpgradeDialog from './AutotraderUpgradeDialog'
import ReconnectMetaApiAccountDialog from '../../../components/dialogs/ReconnectMetaApiAccountDialog'
import { NotificationIconWithMenu } from './AlertNotificationIcon'
import RecentAlertsList from './RecentAlertsList'
import AccountPerformance from './AccountPerformance'
import { useAlertRecentHistory } from '../hooks/useAlertRecentHistory'
import { COMPOSITE_GATE_MESSAGE, compositeGateActive } from '../../../common/utils/composite'
import { captureAlertActiveToggled } from '../../../common/analytics/alertEvents'

interface AlertCardProps {
  alert: Alert
  readOnly?: boolean
}

// Fixed so grid rows stay even; the recent-alerts list scrolls if it needs more room
const CARD_HEIGHT = 360
const MAX_RECENT_ALERTS = 3
// Retry schedule for picking up a trade record that lands after its fire
const TRADE_REFRESH_DELAYS_MS = [0, 15_000, 45_000]

function AlertCard({ alert, readOnly }: AlertCardProps) {
  const theme = useTheme()

  const {
    canActivateAlerts,
    canAutotradeAlert,
    canEnableAutoTrader,
    symbols,
    usermeta,
    setAlertAutoTradeEnabled,
    setActiveTab,
  } = useApplicationSuiteContext()

  const {
    selectedAlert,
    isSidePanelOpen,
    openSidePanel,
    closeSidePanel,
    setSelectedAlert,
    setActiveSidePanelTab,
    enterEditMode,
    scrollToSelectedAlert,
    tradeSummaries,
    tradeSummariesLoading,
    refreshTradeSummaries,
  } = useAlertContext()

  if (readOnly) {
    /*
      When in readOnly view we want alert's status to show
     */
    alert.active = true
  }

  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const { setIsPhoneValidationDialogOpen, setDefaultAlert, setIsTradingAccountDialogOpen } =
    useModalContext()

  const [updatingAlertType, setUpdatingAlertType] = useState(false)
  const [updatingWebhook, setUpdatingWebhook] = useState(false)
  const [showAutotraderUpgradeDialog, setShowAutotraderUpgradeDialog] = useState(false)
  const [showReconnectDialog, setShowReconnectDialog] = useState(false)

  // Check if this card is selected
  const isSelected = selectedAlert?.id === alert.id && isSidePanelOpen

  // Determine autotrader status
  const hasAutotraderAccess = canAutotradeAlert(alert)
  const hasTradingConfig = !!alert.trading?.accountId
  const hasSharedTradingOptions = !!alert.trading?.options && !hasTradingConfig
  const isAutotraderActive = alert.alertTypes?.includes('webhook')
  const canEnableThisAutoTrader = useMemo(
    () => isAutotraderActive || canEnableAutoTrader(alert),
    [isAutotraderActive, canEnableAutoTrader, alert]
  )

  // Auto-trade cards show account performance plus the latest fire; alert-only cards show a list
  const showsPerformance = !readOnly && hasTradingConfig
  const { items: recentItems, loading: recentLoading } = useAlertRecentHistory(
    readOnly ? undefined : alert.id,
    MAX_RECENT_ALERTS
  )
  // Nothing to show yet: not running, never fired, no trading account
  const isEmptyState =
    !readOnly && !alert.active && !showsPerformance && !recentLoading && recentItems.length === 0
  const tradeSummary = tradeSummaries[alert.id]
  const tradesByNotificationId = tradeSummary?.tradesByNotificationId

  // A fire newer than the loaded summary means a trade may have been placed. The trade record can
  // land well after the fire (broker latency), so retry a few times with growing delays until it shows.
  const tradeRefreshAttempts = useRef<{ notificationId: string | null; count: number }>({
    notificationId: null,
    count: 0,
  })
  const latestNotificationId = recentItems[0]?.id
  useEffect(() => {
    if (
      !showsPerformance ||
      !tradeSummary ||
      !latestNotificationId ||
      tradeSummary.tradesByNotificationId[latestNotificationId]
    ) {
      return
    }
    const attempts = tradeRefreshAttempts.current
    if (attempts.notificationId !== latestNotificationId) {
      tradeRefreshAttempts.current = { notificationId: latestNotificationId, count: 0 }
    }
    if (tradeRefreshAttempts.current.count >= TRADE_REFRESH_DELAYS_MS.length) return

    const delay = TRADE_REFRESH_DELAYS_MS[tradeRefreshAttempts.current.count]
    tradeRefreshAttempts.current.count += 1
    const timer = setTimeout(refreshTradeSummaries, delay)
    return () => clearTimeout(timer)
  }, [showsPerformance, tradeSummary, latestNotificationId, refreshTradeSummaries])

  // The list sorts active alerts first, so activating moves this card. Scroll to it again once
  // the Firestore snapshot with the flipped flag has re-rendered it in its new position.
  const scrollAfterActivate = useRef(false)
  useEffect(() => {
    if (!scrollAfterActivate.current || !alert.active) return
    scrollAfterActivate.current = false
    scrollToSelectedAlert(alert)
  }, [alert, scrollToSelectedAlert])

  // Get the connected account's deployment state
  const connectedAccount = useMemo(() => {
    if (!alert.trading?.accountId) return null
    return usermeta.metaapi?.accounts?.find(
      (acc: any) => acc.accountId === alert.trading?.accountId
    )
  }, [alert.trading?.accountId, usermeta.metaapi?.accounts])

  const accountDeploymentState = connectedAccount?.deploymentState

  const isStock = useMemo(() => {
    const symbol = symbols?.find((symbol: any) => symbol.name === alert.symbol)
    return symbol && symbol.type === 'stock'
  }, [symbols, alert.symbol])

  // Handle settings button click
  const handleSettingsClick = () => {
    if (isSelected) {
      closeSidePanel()
    } else {
      openSidePanel(alert)
    }
  }

  const handleOpenHistory = useCallback(() => {
    setActiveSidePanelTab('history')
    openSidePanel(alert)
  }, [alert, openSidePanel, setActiveSidePanelTab])

  const handleOpenEdit = useCallback(() => {
    setSelectedAlert(alert)
    enterEditMode(alert.id)
  }, [alert, setSelectedAlert, enterEditMode])

  // Function to toggle alert types
  const toggleAlertType = useCallback(
    async (alertType: 'email' | 'sms' | 'app' | 'webhook') => {
      setUpdatingAlertType(true)
      if (!alert.id || updatingAlertType) return

      // Create a new alertTypes array
      let newAlertTypes = [...(alert.alertTypes || [])]

      // Check if the alert type is already included
      if (newAlertTypes.includes(alertType)) {
        // Remove it if already present
        newAlertTypes = newAlertTypes.filter((type) => type !== alertType)
      } else {
        // Add it if not present
        newAlertTypes.push(alertType)
      }

      const message =
        alertType === 'email'
          ? 'Email'
          : alertType === 'sms'
            ? 'SMS'
            : alertType === 'webhook'
              ? 'Auto Trading'
              : 'In-app'

      // Update the alert document in Firebase
      try {
        const alertRef = doc(firebase_firestore, 'tradingPlans', alert.id)
        await updateDoc(alertRef, {
          alertTypes: newAlertTypes,
        })

        setSelectedAlert({
          ...alert,
          alertTypes: newAlertTypes,
        })
        setSnackbarSettings({
          severity: 'success',
          message: `${message} notifications ${newAlertTypes.includes(alertType) ? 'enabled' : 'disabled'}!`,
          autoHideDuration: 6000,
        })
      } catch (error) {
        console.error('Error updating alert types:', error)
        setSnackbarSettings({
          severity: 'error',
          message: `Error updating ${message}`,
          autoHideDuration: 6000,
        })
      } finally {
        setUpdatingAlertType(false)
      }
    },
    [alert, updatingAlertType, setSelectedAlert, setSnackbarSettings]
  )

  const handlePhoneVerification = useCallback(() => {
    setDefaultAlert(alert)
    setIsPhoneValidationDialogOpen(true)
  }, [alert, setDefaultAlert, setIsPhoneValidationDialogOpen])

  const handleAutotraderUpgrade = useCallback(() => {
    setShowAutotraderUpgradeDialog(true)
  }, [])

  const handleAutotraderConfigure = useCallback(() => {
    setDefaultAlert(alert)
    setIsTradingAccountDialogOpen(true)
  }, [alert, setDefaultAlert, setIsTradingAccountDialogOpen])

  const handleAutotradeToggle = useCallback(
    async (enable: boolean) => {
      if (!alert.id || updatingWebhook) return
      setUpdatingWebhook(true)
      try {
        const result: any = await setAlertAutoTradeEnabled({ alertId: alert.id, enabled: enable })
        if (result?.data?.status === 200) {
          const base = alert.alertTypes || []
          const newAlertTypes: Alert['alertTypes'] = enable
            ? ([...new Set([...base, 'webhook'])] as Alert['alertTypes'])
            : base.filter((t) => t !== 'webhook')
          setSelectedAlert({ ...alert, alertTypes: newAlertTypes })
          setSnackbarSettings({
            severity: 'success',
            message: `Auto Trading ${enable ? 'enabled' : 'disabled'}!`,
            autoHideDuration: 6000,
          })
        } else {
          const msg = result?.data?.message || 'Failed to update Auto Trading'
          setSnackbarSettings({ severity: 'error', message: msg, autoHideDuration: 6000 })
        }
      } catch (error) {
        console.error('Error toggling autotrader:', error)
        setSnackbarSettings({
          severity: 'error',
          message: 'Error updating Auto Trading',
          autoHideDuration: 6000,
        })
      } finally {
        setUpdatingWebhook(false)
      }
    },
    [alert, updatingWebhook, setAlertAutoTradeEnabled, setSelectedAlert, setSnackbarSettings]
  )

  const handleToggleActive = async (newValue: boolean) => {
    if (!alert.id || (!alert.active && !canActivateAlerts)) return

    // Composite-bearing patterns can't be evaluated by the alert engine yet
    if (newValue && alert.pattern && compositeGateActive(alert.pattern)) {
      setSnackbarSettings({ severity: 'info', message: COMPOSITE_GATE_MESSAGE })
      return
    }

    // Activating selects the card like View Settings; the list re-sorts active alerts first,
    // so the scroll is deferred until the flipped flag comes back from Firestore
    if (newValue) {
      scrollAfterActivate.current = true
      openSidePanel(alert)
    }

    try {
      const alertRef = doc(firebase_firestore, 'tradingPlans', alert.id)
      await updateDoc(alertRef, {
        active: newValue,
      })
      captureAlertActiveToggled(alert, newValue, 'card')

      // Update local state
      setSelectedAlert({
        ...alert,
        active: newValue,
      })

      // Show toast notification
      setSnackbarSettings({
        severity: 'success',
        message: `Alert ${newValue ? 'activated' : 'deactivated'} successfully`,
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    } catch (error) {
      console.error('Error updating alert active state:', error)

      // Show error toast
      setSnackbarSettings({
        severity: 'error',
        message: `Failed to ${newValue ? 'activate' : 'deactivate'} alert`,
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    }
  }

  // Define actions for each notification type
  const emailActions = [
    {
      icon: alert.alertTypes?.includes('email') ? <ToggleOffIcon /> : <ToggleOnIcon />,
      label: alert.alertTypes?.includes('email') ? 'Deactivate' : 'Activate',
      onClick: () => toggleAlertType('email'),
      disabled: updatingAlertType,
    },
  ]

  const phoneActions = [
    // Show verify only if no phone number
    ...(!alert.user?.phoneNumber
      ? [
          {
            icon: <VerifiedUserIcon />,
            label: 'Verify Phone',
            onClick: handlePhoneVerification,
            disabled: false,
          },
        ]
      : []),
    // Always show toggle if phone number exists
    ...(alert.user?.phoneNumber
      ? [
          {
            icon: alert.alertTypes?.includes('sms') ? <ToggleOffIcon /> : <ToggleOnIcon />,
            label: alert.alertTypes?.includes('sms') ? 'Deactivate' : 'Activate',
            onClick: () => toggleAlertType('sms'),
            disabled: updatingAlertType,
          },
        ]
      : []),
    // Always show update phone number option
    {
      icon: <EditIcon />,
      label: 'Update Phone',
      onClick: handlePhoneVerification,
      disabled: false,
    },
  ]

  // Define autotrader actions based on access and configuration
  const autotraderActions = React.useMemo(() => {
    // User doesn't have autotrader permission for this alert - show upgrade option
    if (!hasAutotraderAccess) {
      return [
        {
          icon: <AttachMoneyIcon />,
          label: 'Upgrade to Auto Trade',
          onClick: handleAutotraderUpgrade,
          disabled: false,
        },
      ]
    }

    const actions = []

    if (hasTradingConfig) {
      // Has full trading config (account connected)

      // Show account connection status if available
      if (connectedAccount) {
        const statusLabel =
          accountDeploymentState === 'DEPLOYING'
            ? 'Connecting...'
            : accountDeploymentState === 'UNDEPLOYING'
              ? 'Disconnecting...'
              : accountDeploymentState === 'UNDEPLOYED'
                ? 'Disconnected'
                : 'Connected'

        actions.push({
          icon: (
            <FiberManualRecordIcon
              sx={{
                fontSize: 12,
                color:
                  accountDeploymentState === 'DEPLOYING' || accountDeploymentState === 'UNDEPLOYING'
                    ? theme.palette.warning.main
                    : accountDeploymentState === 'UNDEPLOYED'
                      ? theme.palette.error.main
                      : theme.palette.success.main,
              }}
            />
          ),
          label: `Account: ${statusLabel}`,
          onClick: () => {}, // No-op, just informational
          disabled: true,
        })

        if (accountDeploymentState === 'UNDEPLOYED') {
          actions.push({
            icon: <RestartAltIcon />,
            label: 'Reset Account',
            onClick: () => setShowReconnectDialog(true),
            disabled: false,
          })
        }
      }

      if (isAutotraderActive) {
        actions.push({
          icon: <ToggleOffIcon />,
          label: 'Deactivate',
          onClick: () => handleAutotradeToggle(false),
          disabled: updatingWebhook,
        })
      } else if (canEnableThisAutoTrader) {
        actions.push({
          icon: <ToggleOnIcon />,
          label: 'Activate',
          onClick: () => handleAutotradeToggle(true),
          disabled: updatingWebhook || accountDeploymentState === 'UNDEPLOYED',
        })
      } else {
        actions.push({
          icon: <AttachMoneyIcon />,
          label: 'Limit reached — Upgrade',
          onClick: handleAutotraderUpgrade,
          disabled: false,
        })
      }
      actions.push({
        icon: <TuneIcon />,
        label: 'Edit Trade Parameters',
        onClick: handleAutotraderConfigure,
        disabled: false,
      })
    } else if (hasSharedTradingOptions) {
      // Has shared trading options but no account (imported shared alert)
      actions.push({
        icon: <AccountBalance />,
        label: 'Connect Account to Trade',
        onClick: handleAutotraderConfigure,
        disabled: false,
      })
    } else {
      // No trading config at all
      actions.push({
        icon: <AccountBalance />,
        label: 'Connect MetaTrader',
        onClick: handleAutotraderConfigure,
        disabled: false,
      })
    }

    return actions
  }, [
    hasAutotraderAccess,
    canEnableThisAutoTrader,
    hasTradingConfig,
    hasSharedTradingOptions,
    isAutotraderActive,
    updatingWebhook,
    handleAutotraderUpgrade,
    handleAutotraderConfigure,
    handleAutotradeToggle,
    connectedAccount,
    accountDeploymentState,
    theme,
    setShowReconnectDialog,
  ])

  const autotraderIcon = !hasAutotraderAccess ? (
    <Badge
      badgeContent={<LockIcon sx={{ fontSize: 12 }} />}
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: 'transparent',
          color: theme.palette.warning.main,
        },
      }}
    >
      <AttachMoneyIcon />
    </Badge>
  ) : isAutotraderActive && accountDeploymentState === 'UNDEPLOYED' ? (
    <Badge
      badgeContent={<WarningAmberIcon sx={{ fontSize: 12, color: theme.palette.warning.main }} />}
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: 'transparent',
          color: theme.palette.warning.main,
        },
      }}
    >
      <AttachMoneyIcon />
    </Badge>
  ) : (
    <AttachMoneyIcon />
  )

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: readOnly ? '100%' : CARD_HEIGHT,
        '--Paper-overlay': 'none !important',
        transition: 'all 0.3s ease',
        border: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
        // Ensure card content can overflow for menus
        overflow: 'visible',
      }}
      className={'alert-card'}
      data-alert-id={alert.id}
    >
      {/* Header Section */}
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={0.5}
        sx={{
          padding: '8px 12px 4px 12px',
          backgroundColor: theme.palette.background.custom4 || '#1e1e1e',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          position: 'relative',
        }}
      >
        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
          <Box
            display={'flex'}
            alignItems={'center'}
            overflow="hidden"
            gap={1}
            data-tour-id="alert-name"
          >
            {alert.active ? (
              <NotificationsActiveIcon fontSize={'small'} />
            ) : (
              <NotificationsIcon fontSize={'small'} />
            )}
            <Typography
              variant="body1"
              color="text.primary"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              onClick={(e) => {
                e.stopPropagation()
                handleOpenEdit()
              }}
              sx={{
                cursor: 'pointer',
                borderRadius: 1,
                px: 0.5,
                mx: -0.5,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              {alert.name}
            </Typography>
          </Box>
          {/* Active Status Indicator */}
          {!readOnly && (
            <SettingsSwitchRow
              disabled={!alert.active && !canActivateAlerts}
              isActive={alert.active || false}
              onChange={handleToggleActive}
              size="small"
            />
          )}
        </Box>

        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" gap={1}>
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            minWidth={0}
            data-tour-id="alert-pattern-name"
          >
            <AccountTreeIcon
              fontSize={'small'}
              sx={{
                color: theme.palette.text.secondary,
              }}
            />
            <PatternVersionTitle
              pattern={alert.pattern!}
              symbol={alert.pattern!.trigger.chartDetails.symbol}
              color="text.secondary"
              onClick={(e) => {
                e.stopPropagation()
                setActiveTab({ tab: AppTabs.PatternBuilder, options: { item: alert.pattern } })
              }}
            />
          </Box>

          {/* Notification type icons */}
          <Box
            data-tour-id="alert-type-icons"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, pr: 0.5 }}
          >
            <NotificationIconWithMenu
              compact
              icon={<EmailIcon />}
              label="Email"
              status={
                readOnly ? 'active' : alert.alertTypes?.includes('email') ? 'active' : 'inactive'
              }
              actions={emailActions}
              disabled={updatingAlertType}
              readOnly={readOnly}
            />

            <NotificationIconWithMenu
              compact
              icon={<PhoneIcon />}
              label="Phone"
              status={
                readOnly
                  ? 'active'
                  : !alert.user?.phoneNumber
                    ? 'needsSetup'
                    : alert.alertTypes?.includes('sms')
                      ? 'active'
                      : 'inactive'
              }
              actions={phoneActions}
              disabled={updatingAlertType}
              readOnly={readOnly}
            />

            {/* Auto trade - always shown for non-stock owners (locked without permission) */}
            {!isStock && !readOnly && (
              <NotificationIconWithMenu
                compact
                icon={autotraderIcon}
                label="Auto Trade"
                status={
                  !hasAutotraderAccess
                    ? 'inactive'
                    : !hasTradingConfig
                      ? 'needsSetup'
                      : isAutotraderActive && accountDeploymentState === 'UNDEPLOYED'
                        ? 'warning'
                        : isAutotraderActive
                          ? 'active'
                          : 'inactive'
                }
                actions={autotraderActions}
                disabled={updatingAlertType}
                readOnly={readOnly}
                isLoading={updatingAlertType}
              />
            )}

            {/* For readOnly mode, only show if webhook is active */}
            {!isStock && readOnly && alert.alertTypes?.includes('webhook') && (
              <NotificationIconWithMenu
                compact
                icon={<AttachMoneyIcon />}
                label="Auto Trade"
                status="active"
                actions={[]}
                disabled={true}
                readOnly={readOnly}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Content Section */}
      <CardContent
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 1.5,
          '&:last-child': { pb: 1.5 },
          backgroundColor: theme.palette.background.custom3 || '#121212',
          // Allow overflow for menus
          overflow: 'visible',
        }}
      >
        {/* Status Section - live condition progress, only meaningful while the alert runs */}
        {alert.active && (
          <Box data-tour-id="alert-content-section">
            <AlertConditionStatus pattern={alert.pattern!} isActive compact />
          </Box>
        )}

        {isEmptyState ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 0.5,
              px: 2,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<NotificationsActiveIcon />}
              disabled={!canActivateAlerts}
              onClick={(e) => {
                e.stopPropagation()
                handleToggleActive(true)
              }}
              sx={{ my: 0.5, px: 1.5, fontSize: '12px' }}
            >
              Activate
            </Button>
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
              Activate this alert to start getting notifications.
            </Typography>
          </Box>
        ) : (
          <>
            {!readOnly && showsPerformance && (
              <AccountPerformance
                stats={tradeSummary?.stats ?? null}
                loading={tradeSummariesLoading && !tradeSummary}
                onTitleClick={handleOpenEdit}
              />
            )}

            {!readOnly && (
              <RecentAlertsList
                items={recentItems}
                tradesByNotificationId={tradesByNotificationId}
                loading={recentLoading}
                onTitleClick={handleOpenHistory}
              />
            )}
          </>
        )}
      </CardContent>

      {/* Settings Button */}
      {!readOnly && (
        <CardActions
          sx={{
            position: 'relative',
            height: 40,
            flexShrink: 0,
            background: theme.palette.background.custom3 || '#121212',
            p: 0,
            px: 1,
            py: 0.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={handleSettingsClick}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              color: isSelected ? theme.palette.error.main : theme.palette.primary.main,
              transition: 'all 0.2s ease',
            }}
            endIcon={isSelected ? <CloseIcon /> : <SettingsIcon />}
            data-tour-id="alert-settings-button"
          >
            {isSelected ? 'Close Settings' : 'View Settings'}
          </Button>
        </CardActions>
      )}

      {/* Autotrader Upgrade Dialog */}
      <AutotraderUpgradeDialog
        open={showAutotraderUpgradeDialog}
        onClose={() => setShowAutotraderUpgradeDialog(false)}
      />

      {showReconnectDialog && connectedAccount && (
        <ReconnectMetaApiAccountDialog
          open={showReconnectDialog}
          accounts={[{ ...connectedAccount, platform: connectedAccount.platform as 'mt4' | 'mt5' }]}
          onClose={() => setShowReconnectDialog(false)}
        />
      )}
    </Card>
  )
}

export default AlertCard
